import { UitkLayoutGrid, UitkLayoutGridItem } from "@egds/react-core/layout-grid";
import { UitkIcon, UitkIconSize } from "@egds/react-core/icons";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "@egds/react-core/layout-flex";
import { UitkCard, UitkCardContentSection, UitkCardLink } from "@egds/react-core/cards";
import * as React from "react";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "@egds/react-core/images";
import { observer } from "mobx-react";
import { FlightCardDefaultContentProps } from "components/flexComponents/Flights/typings";
import { PriceLockup } from "components/shared/PriceLockup/PriceLockup";
import { locKeys } from "./l10";
import { getElapsedTime } from "components/flexComponents/Flights/util/getElapsedTime";
import { TripType } from "components/flexComponents/Flights/util/typings";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { UitkSpacing } from "@egds/react-core/spacing";
import { UitkText } from "@egds/react-core/text";

export const FlightCardDefaultContent: React.FC<FlightCardDefaultContentProps> = observer(
  ({ flight, index, isMobile, showCityName }) => {
    const { formatText } = useLocalization();
    const isOneWay = flight.tripType === TripType.ONE_WAY;
    const airCarrier = flight.airCarrier?.name || "Bargain Flight";
    const {
      airCarrierImageUrl,
      searchUrl,
      formattedDepartureDate,
      formattedReturnDate,
      formattedTicketPrice,
      elapsedHours,
    } = flight;
    const tripTypeText = isOneWay ? formatText(locKeys.flightOneWayText) : formatText(locKeys.flightRoundTripText);

    const originCode = flight.origin.code;
    const destinationCode = flight.destination.code;
    const originAirPort = showCityName ? flight.origin.cityName : flight.origin.name;
    const destinationAirPort = showCityName ? flight.destination.cityName : flight.destination.name;
    const elapsedDays = Math.floor(elapsedHours / 24);
    const formattedElapsedTime = getElapsedTime(elapsedHours, elapsedDays);
    const travelDate = isOneWay ? formattedDepartureDate : `${formattedDepartureDate} - ${formattedReturnDate}`;
    const accessibilityMessageCard = isOneWay
      ? formatText(
          locKeys.flightAccessibilityMessageOneWay,
          airCarrier,
          formattedDepartureDate,
          originAirPort,
          destinationAirPort,
          formattedTicketPrice,
          formattedElapsedTime
        )
      : formatText(
          locKeys.flightAccessibilityMessageRoundTrip,
          airCarrier,
          formattedDepartureDate,
          originAirPort,
          destinationAirPort,
          formattedReturnDate,
          formattedTicketPrice,
          formattedElapsedTime
        );
    const arrowIconSize = isMobile ? UitkIconSize.SMALL : UitkIconSize.MEDIUM;
    const airlineLogoBlockSpace = isMobile ? "one" : "three";
    const airlineLogoShrink = isMobile ? 1 : 0;
    const airlineLogoRatio = isMobile ? UitkFigureAspectRatioType.R21_9 : UitkFigureAspectRatioType.R1_1;
    const arrowIcon = isOneWay ? (
      <UitkIcon name="arrow_forward" size={arrowIconSize} />
    ) : (
      <UitkIcon name="swap_horiz" size={arrowIconSize} />
    );
    const airLineIcon = (
      <UitkSpacing padding={{ inline: airlineLogoBlockSpace, block: airlineLogoBlockSpace }}>
        <UitkLayoutFlexItem maxWidth={100} minWidth={100} shrink={airlineLogoShrink}>
          <div>
            <UitkFigure imageFit="contain" ratio={airlineLogoRatio}>
              <UitkImage src={airCarrierImageUrl} alt={airCarrier} lazyLoading="lazy" />
            </UitkFigure>
          </div>
        </UitkLayoutFlexItem>
      </UitkSpacing>
    );
    const dateAndLocations = (
      <div>
        <UitkText size={300} className="DateRange" weight="bold">
          {travelDate}
        </UitkText>
        <UitkSpacing margin={{ blockstart: "two" }}>
          <UitkLayoutGrid columns={4} space="two">
            <>
              <UitkLayoutGridItem>
                <div>
                  <UitkText size={500}>{originCode}</UitkText>
                  <UitkText size={100} overflow="truncate">
                    {originAirPort}
                  </UitkText>
                </div>
              </UitkLayoutGridItem>
              <UitkLayoutGridItem>{arrowIcon}</UitkLayoutGridItem>
              <UitkLayoutGridItem>
                <div>
                  <UitkText size={500}>{destinationCode}</UitkText>
                  <UitkText size={100} overflow="truncate">
                    {destinationAirPort}
                  </UitkText>
                </div>
              </UitkLayoutGridItem>
            </>
          </UitkLayoutGrid>
        </UitkSpacing>
      </div>
    );
    const priceDetails = (
      <PriceLockup
        lockupPrice={formattedTicketPrice}
        priceSubtextFirst={tripTypeText}
        priceSubtextStandard={getElapsedTime(elapsedHours, elapsedDays)}
        a11yPrice={`${formattedTicketPrice} ${tripTypeText}, ${formattedElapsedTime}`}
      />
    );

    return isMobile ? (
      <UitkCard border>
        <UitkLayoutFlex alignItems="center">
          <UitkLayoutFlexItem grow={1}>
            <UitkCardContentSection>
              <UitkLayoutGrid columns={6} space="two">
                <>
                  <UitkLayoutGridItem colSpan={2}>{airLineIcon}</UitkLayoutGridItem>
                  <UitkLayoutGridItem colSpan={4}>
                    <UitkLayoutFlex justifyContent="end">{priceDetails}</UitkLayoutFlex>
                  </UitkLayoutGridItem>
                  <UitkLayoutGridItem colSpan={4}>{dateAndLocations}</UitkLayoutGridItem>
                </>
              </UitkLayoutGrid>
              <UitkCardLink>
                <TrackedLink
                  moduleName="Flights"
                  href={searchUrl}
                  rfrr={`flightDefaultCard-${index}`}
                  data-testid={`flightcard-link-${index}`}
                >
                  {accessibilityMessageCard}
                </TrackedLink>
              </UitkCardLink>
            </UitkCardContentSection>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkCard>
    ) : (
      <UitkCard border>
        <UitkLayoutFlex alignItems="center">
          <>
            {airLineIcon}
            <UitkLayoutFlexItem grow={1}>
              <UitkCardContentSection>
                <UitkLayoutGrid columns={6} space="two">
                  <>
                    <UitkLayoutGridItem colSpan={4}>{dateAndLocations}</UitkLayoutGridItem>
                    <UitkLayoutGridItem colSpan={2}>
                      <UitkLayoutFlex justifyContent="end">{priceDetails}</UitkLayoutFlex>
                    </UitkLayoutGridItem>
                  </>
                </UitkLayoutGrid>
                <UitkCardLink>
                  <TrackedLink moduleName="Flights" href={searchUrl} rfrr={`flightDefaultCard-${index}`}>
                    {accessibilityMessageCard}
                  </TrackedLink>
                </UitkCardLink>
              </UitkCardContentSection>
            </UitkLayoutFlexItem>
          </>
        </UitkLayoutFlex>
      </UitkCard>
    );
  }
);
